// src/services/api/endpoints/caas.ts
import {
  CaasClient,
  CaasTeam,
  CaasClientCreateRequest,
  CaasClientUpdateRequest,
  CaasTeamCreateRequest,
  CaasTeamUpdateRequest,
  FabrikUser,
  Space,
  BulkTeamImportItem,
  BulkTeamImportRequest,
  BulkTeamImportResponse,
  BulkTeamImportResultItem,
} from '../../../types';

import { API_BASE_URL, fetchWithTokenRefresh } from '../client';
import { 
  generateCacheKey,
  getCacheItem,
  setCacheItem,
  invalidateCache
} from '../cache';

// ============================================================================
// CaaS Client Operations
// ============================================================================

/**
 * Fetches all CaaS clients
 * @param token Firebase authentication token
 * @returns Promise containing array of clients
 */
export const listClients = async (token: string): Promise<CaasClient[]> => {
  try {
    const url = `${API_BASE_URL}/caas/clients`;
    const cacheKey = generateCacheKey(url);
    
    // Check cache
    const cachedData = getCacheItem<CaasClient[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('Error fetching CaaS clients:', error);
    throw error;
  }
};

/**
 * Fetches a specific CaaS client by ID
 * @param token Firebase authentication token
 * @param clientId The client ID
 * @returns Promise containing the client
 */
export const getClient = async (token: string, clientId: string): Promise<CaasClient> => {
  try {
    const url = `${API_BASE_URL}/caas/clients/${clientId}`;
    const cacheKey = generateCacheKey(url);
    
    // Check cache
    const cachedData = getCacheItem<CaasClient>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error(`Error fetching CaaS client ${clientId}:`, error);
    throw error;
  }
};

/**
 * Creates a new CaaS client
 * @param token Firebase authentication token
 * @param clientData Client data to create
 * @returns Promise containing the created client
 */
export const createClient = async (
  token: string,
  clientData: CaasClientCreateRequest
): Promise<CaasClient> => {
  try {
    const url = `${API_BASE_URL}/caas/clients`;
    
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clientData),
    });
    
    // Invalidate clients list cache
    invalidateCache(`${API_BASE_URL}/caas/clients`);
    
    return data;
  } catch (error) {
    console.error('Error creating CaaS client:', error);
    throw error;
  }
};

/**
 * Updates an existing CaaS client
 * @param token Firebase authentication token
 * @param clientId The client ID to update
 * @param clientData Client data to update
 * @returns Promise containing the updated client
 */
export const updateClient = async (
  token: string,
  clientId: string,
  clientData: CaasClientUpdateRequest
): Promise<CaasClient> => {
  try {
    const url = `${API_BASE_URL}/caas/clients/${clientId}`;
    
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clientData),
    });
    
    // Invalidate caches
    invalidateCache(`${API_BASE_URL}/caas/clients`);
    invalidateCache(`${API_BASE_URL}/caas/clients/${clientId}`);
    
    return data;
  } catch (error) {
    console.error(`Error updating CaaS client ${clientId}:`, error);
    throw error;
  }
};

/**
 * Deletes a CaaS client
 * @param token Firebase authentication token
 * @param clientId The client ID to delete
 */
export const deleteClient = async (token: string, clientId: string): Promise<void> => {
  try {
    const url = `${API_BASE_URL}/caas/clients/${clientId}`;
    
    await fetchWithTokenRefresh(url, token, {
      method: 'DELETE',
    });
    
    // Invalidate caches
    invalidateCache(`${API_BASE_URL}/caas/clients`);
    invalidateCache(`${API_BASE_URL}/caas/clients/${clientId}`);
  } catch (error) {
    console.error(`Error deleting CaaS client ${clientId}:`, error);
    throw error;
  }
};

/**
 * Fetches spaces associated with a specific client
 * @param token Firebase authentication token
 * @param clientId The client ID
 * @returns Promise containing array of spaces
 */
export const getClientSpaces = async (token: string, clientId: string): Promise<Space[]> => {
  try {
    const url = `${API_BASE_URL}/caas/clients/${clientId}/spaces`;
    const cacheKey = generateCacheKey(url);
    
    // Check cache
    const cachedData = getCacheItem<Space[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error(`Error fetching spaces for client ${clientId}:`, error);
    throw error;
  }
};

// ============================================================================
// CaaS Team Operations
// ============================================================================

/**
 * Fetches all CaaS teams
 * @param token Firebase authentication token
 * @returns Promise containing array of teams
 */
export const listTeams = async (token: string): Promise<CaasTeam[]> => {
  try {
    const url = `${API_BASE_URL}/caas/teams`;
    const cacheKey = generateCacheKey(url);
    
    // Check cache
    const cachedData = getCacheItem<CaasTeam[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('Error fetching CaaS teams:', error);
    throw error;
  }
};

/**
 * Fetches a specific CaaS team by ID
 * @param token Firebase authentication token
 * @param teamId The team ID
 * @returns Promise containing the team
 */
export const getTeam = async (token: string, teamId: string): Promise<CaasTeam> => {
  try {
    const url = `${API_BASE_URL}/caas/teams/${teamId}`;
    const cacheKey = generateCacheKey(url);
    
    // Check cache
    const cachedData = getCacheItem<CaasTeam>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error(`Error fetching CaaS team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Creates a new CaaS team
 * @param token Firebase authentication token
 * @param teamData Team data to create
 * @returns Promise containing the created team
 */
export const createTeam = async (
  token: string,
  teamData: CaasTeamCreateRequest
): Promise<CaasTeam> => {
  try {
    const url = `${API_BASE_URL}/caas/teams`;
    
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(teamData),
    });
    
    // Invalidate caches
    invalidateCache(`${API_BASE_URL}/caas/teams`);
    invalidateCache(`${API_BASE_URL}/caas/clients`); // Client list may show team counts
    
    return data;
  } catch (error) {
    console.error('Error creating CaaS team:', error);
    throw error;
  }
};

/**
 * Updates an existing CaaS team
 * @param token Firebase authentication token
 * @param teamId The team ID to update
 * @param teamData Team data to update
 * @returns Promise containing the updated team
 */
export const updateTeam = async (
  token: string,
  teamId: string,
  teamData: CaasTeamUpdateRequest
): Promise<CaasTeam> => {
  try {
    const url = `${API_BASE_URL}/caas/teams/${teamId}`;
    
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(teamData),
    });
    
    // Invalidate caches
    invalidateCache(`${API_BASE_URL}/caas/teams`);
    invalidateCache(`${API_BASE_URL}/caas/teams/${teamId}`);
    
    return data;
  } catch (error) {
    console.error(`Error updating CaaS team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Deletes a CaaS team
 * @param token Firebase authentication token
 * @param teamId The team ID to delete
 */
export const deleteTeam = async (token: string, teamId: string): Promise<void> => {
  try {
    const url = `${API_BASE_URL}/caas/teams/${teamId}`;
    
    await fetchWithTokenRefresh(url, token, {
      method: 'DELETE',
    });
    
    // Invalidate caches
    invalidateCache(`${API_BASE_URL}/caas/teams`);
    invalidateCache(`${API_BASE_URL}/caas/teams/${teamId}`);
    invalidateCache(`${API_BASE_URL}/caas/clients`); // Client list may show team counts
  } catch (error) {
    console.error(`Error deleting CaaS team ${teamId}:`, error);
    throw error;
  }
};

// ============================================================================
// Team Member Operations
// ============================================================================

/**
 * Fetches members of a specific team
 * @param token Firebase authentication token
 * @param teamId The team ID
 * @returns Promise containing array of team members
 */
export const listTeamMembers = async (token: string, teamId: string): Promise<FabrikUser[]> => {
  try {
    const url = `${API_BASE_URL}/caas/teams/${teamId}/members`;
    const cacheKey = generateCacheKey(url);
    
    // Check cache
    const cachedData = getCacheItem<FabrikUser[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error(`Error fetching members for team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Adds a member to a team
 * @param token Firebase authentication token
 * @param teamId The team ID
 * @param userId The user ID to add
 */
export const addTeamMember = async (
  token: string,
  teamId: string,
  userId: string
): Promise<void> => {
  try {
    const url = `${API_BASE_URL}/caas/teams/${teamId}/members/${userId}`;
    
    await fetchWithTokenRefresh(url, token, {
      method: 'POST',
    });
    
    // Invalidate team members cache
    invalidateCache(`${API_BASE_URL}/caas/teams/${teamId}/members`);
  } catch (error) {
    console.error(`Error adding member ${userId} to team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Removes a member from a team
 * @param token Firebase authentication token
 * @param teamId The team ID
 * @param userId The user ID to remove
 */
export const removeTeamMember = async (
  token: string,
  teamId: string,
  userId: string
): Promise<void> => {
  try {
    const url = `${API_BASE_URL}/caas/teams/${teamId}/members/${userId}`;
    
    await fetchWithTokenRefresh(url, token, {
      method: 'DELETE',
    });
    
    // Invalidate team members cache
    invalidateCache(`${API_BASE_URL}/caas/teams/${teamId}/members`);
  } catch (error) {
    console.error(`Error removing member ${userId} from team ${teamId}:`, error);
    throw error;
  }
};

// ============================================================================
// Bulk Team Import Operations
// ============================================================================

/**
 * Calculates safe chunk size for bulk imports to stay under AWS WAF 8KB limit
 * @param teams Array of teams to chunk
 * @param maxSizeBytes Maximum payload size in bytes (default 6KB for safety margin)
 * @returns Optimal chunk size
 */
const calculateChunkSize = (teams: BulkTeamImportItem[], maxSizeBytes: number = 6144): number => {
  if (teams.length === 0) return 0;
  
  // Estimate average team size
  const sampleTeam = teams[0];
  const sampleSize = new Blob([JSON.stringify({ teams: [sampleTeam] })]).size;
  const estimatedTeamSize = sampleSize - 12; // Subtract JSON wrapper overhead
  
  // Calculate how many teams fit in the size limit
  const chunkSize = Math.max(1, Math.floor(maxSizeBytes / estimatedTeamSize));
  
  return chunkSize;
};

/**
 * Bulk imports teams for a specific client (single chunk)
 * @param token Firebase authentication token
 * @param clientId The client ID to import teams for
 * @param teams Array of teams to import (max 500)
 * @returns Promise containing import results with success/failure details per team
 */
export const bulkImportTeams = async (
  token: string,
  clientId: string,
  teams: BulkTeamImportItem[]
): Promise<BulkTeamImportResponse> => {
  try {
    const url = `${API_BASE_URL}/caas/clients/${clientId}/teams/bulk-import`;
    
    const payload: BulkTeamImportRequest = { teams };
    
    const data = await fetchWithTokenRefresh(url, token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    // Invalidate relevant caches after successful import
    invalidateCache(`${API_BASE_URL}/caas/teams`);
    invalidateCache(`${API_BASE_URL}/caas/clients`);
    invalidateCache(`${API_BASE_URL}/caas/clients/${clientId}`);
    
    return data;
  } catch (error) {
    console.error(`Error bulk importing teams for client ${clientId}:`, error);
    throw error;
  }
};

/**
 * Bulk imports teams in chunks with progress callback
 * @param token Firebase authentication token
 * @param clientId The client ID to import teams for
 * @param teams Array of teams to import
 * @param onProgress Optional callback for progress updates (currentChunk, totalChunks, results)
 * @returns Promise containing aggregated import results
 */
export const bulkImportTeamsChunked = async (
  token: string,
  clientId: string,
  teams: BulkTeamImportItem[],
  onProgress?: (current: number, total: number, results: BulkTeamImportResultItem[]) => void
): Promise<BulkTeamImportResponse> => {
  try {
    // Calculate optimal chunk size to stay under WAF 8KB limit
    const chunkSize = calculateChunkSize(teams);
    
    // Split teams into chunks
    const chunks: BulkTeamImportItem[][] = [];
    for (let i = 0; i < teams.length; i += chunkSize) {
      chunks.push(teams.slice(i, i + chunkSize));
    }
    
    console.log(`[Bulk Import] Splitting ${teams.length} teams into ${chunks.length} chunks of ~${chunkSize} teams each`);
    
    // Process chunks sequentially
    const allResults: BulkTeamImportResultItem[] = [];
    let successfulCount = 0;
    let failedCount = 0;
    
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      console.log(`[Bulk Import] Processing chunk ${i + 1}/${chunks.length} with ${chunk.length} teams`);
      
      try {
        const chunkResponse = await bulkImportTeams(token, clientId, chunk);
        
        // Aggregate results
        allResults.push(...chunkResponse.results);
        successfulCount += chunkResponse.successful;
        failedCount += chunkResponse.failed;
        
        // Call progress callback if provided
        if (onProgress) {
          onProgress(i + 1, chunks.length, allResults);
        }
      } catch (error) {
        console.error(`[Bulk Import] Error processing chunk ${i + 1}:`, error);
        
        // Mark all teams in this chunk as failed
        const failedResults: BulkTeamImportResultItem[] = chunk.map((team, idx) => ({
          row_index: i * chunkSize + idx,
          team_name: team.name,
          success: false,
          error: 'Chunk processing failed',
          error_detail: error instanceof Error ? error.message : String(error),
        }));
        
        allResults.push(...failedResults);
        failedCount += chunk.length;
        
        // Still call progress callback to show partial results
        if (onProgress) {
          onProgress(i + 1, chunks.length, allResults);
        }
      }
    }
    
    console.log(`[Bulk Import] Complete: ${successfulCount} successful, ${failedCount} failed out of ${teams.length} total`);
    
    return {
      total: teams.length,
      successful: successfulCount,
      failed: failedCount,
      results: allResults,
    };
  } catch (error) {
    console.error(`Error in chunked bulk import for client ${clientId}:`, error);
    throw error;
  }
};
